import React, { useState } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { Alert, alpha, Box, Button, Chip, CircularProgress, Skeleton, Typography } from '@mui/material';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import LabelOffOutlinedIcon from '@mui/icons-material/LabelOffOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { RecordingButton } from '../calls/RecordingPlayer';
import { describeListError } from '../contacts/contactList';
import { CRM_CONTACT_TIMELINE } from './contactDetail.queries';
import {
  dayHeading,
  entryFacts,
  TIMELINE_KINDS,
  TIMELINE_PAGE,
  TimelineEntry,
  TimelineKind,
  TimelinePage,
} from './contactDetail';

const ICONS: Record<TimelineKind, { Icon: typeof PhoneOutlinedIcon; tint: string }> = {
  CALL: { Icon: PhoneOutlinedIcon, tint: '#2563eb' },
  SMS: { Icon: SmsOutlinedIcon, tint: '#0891b2' },
  TAG: { Icon: LocalOfferOutlinedIcon, tint: '#7c3aed' },
  APP: { Icon: AppsOutlinedIcon, tint: '#16a34a' },
  NOTIFICATION: { Icon: NotificationsNoneOutlinedIcon, tint: '#d97706' },
  NATIONBUILDER: { Icon: HistoryEduOutlinedIcon, tint: '#64748b' },
  LIST: { Icon: PlaylistAddIcon, tint: '#2e7d32' },
  CHANGE: { Icon: EditNoteOutlinedIcon, tint: '#0f766e' },
};

/** Entries that take something away show it with a struck-through icon and a quieter title. */
const REMOVED: Record<string, typeof PhoneOutlinedIcon> = {
  TAG_REMOVED: LabelOffOutlinedIcon,
  LIST_REMOVED: PlaylistRemoveIcon,
};

const n = (value: number) => value.toLocaleString('en-US');

const Entry = ({ entry }: { entry: TimelineEntry }) => {
  const removed = entry.event in REMOVED || Boolean(entry.change?.undo);
  const { Icon, tint } = ICONS[entry.kind] ?? ICONS.NATIONBUILDER;
  const Shown = REMOVED[entry.event] ?? Icon;
  const facts = entryFacts(entry);

  return (
    <Box component="li" sx={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', columnGap: 1.5, py: 1.25 }}>
      <Box
        aria-hidden
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(entry.tag?.color ?? tint, 0.12),
          color: entry.tag?.color ?? tint,
          '& svg': { fontSize: 17 },
        }}
      >
        <Shown />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, ...(removed ? { color: 'text.secondary' } : {}) }}>
          {entry.title}
        </Typography>
        {facts.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="p">
            {facts.join(' · ')}
          </Typography>
        )}
        {entry.body && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'text.primary',
              bgcolor: '#f8fafc',
              border: '1px solid #eef1f5',
              borderRadius: 1.5,
              px: 1.25,
              py: 0.75,
            }}
          >
            {entry.body}
          </Typography>
        )}
        {entry.call?.recordingUrl && (
          <Box sx={{ mt: 0.75 }}>
            <RecordingButton url={entry.call.recordingUrl} />
          </Box>
        )}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="time"
        dateTime={entry.occurredAt}
        sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', pt: 0.25 }}
      >
        {new Date(entry.occurredAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
      </Typography>
    </Box>
  );
};

/** Entries under a heading per day, newest first, as the API orders them. */
const byDay = (items: ReadonlyArray<TimelineEntry>) => {
  const days: Array<{ heading: string; entries: Array<TimelineEntry> }> = [];
  items.forEach((entry) => {
    const heading = dayHeading(entry.occurredAt);
    const last = days[days.length - 1];
    if (last?.heading === heading) last.entries.push(entry);
    else days.push({ heading, entries: [entry] });
  });
  return days;
};

/**
 * The merged activity feed (card 14): calls, texts, tags added and removed, app
 * activity, app notifications and NationBuilder's history, newest first. The
 * chips narrow it to one kind; each says how many there are. Older entries come
 * a page at a time by cursor, appended below.
 *
 * Asked fresh each time the page opens (`cache-and-network`): calls and tags
 * change while nobody is looking at this person.
 */
export const ContactTimeline = ({ contactId }: { contactId: number }) => {
  const [kind, setKind] = useState<TimelineKind | null>(null);

  const { data, error, networkStatus, fetchMore } = useQuery<{ crmContactTimeline: TimelinePage }>(CRM_CONTACT_TIMELINE, {
    variables: { contactId, kinds: kind ? [kind] : null, take: TIMELINE_PAGE, withCounts: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.crmContactTimeline;
  const counts = new Map((page?.counts ?? []).map((c) => [c.kind, c.count]));
  const all = (page?.counts ?? []).reduce((sum, c) => sum + c.count, 0);
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const firstLoad = !page && networkStatus !== NetworkStatus.error;

  const showOlder = () =>
    fetchMore({
      variables: { after: page?.endCursor, withCounts: false },
      updateQuery: (previous, { fetchMoreResult }) => ({
        crmContactTimeline: {
          ...fetchMoreResult.crmContactTimeline,
          items: [...previous.crmContactTimeline.items, ...fetchMoreResult.crmContactTimeline.items],
          counts: previous.crmContactTimeline.counts,
        },
      }),
    });

  const chip = (value: TimelineKind | null, label: string, count: number | undefined) => {
    const on = kind === value;
    return (
      <Chip
        key={value ?? 'all'}
        size="small"
        clickable
        aria-pressed={on}
        color={on ? 'primary' : 'default'}
        variant={on ? 'filled' : 'outlined'}
        label={count === undefined ? label : `${label} ${n(count)}`}
        onClick={() => setKind(value)}
        sx={{ fontVariantNumeric: 'tabular-nums', ...(count === 0 && !on ? { opacity: 0.55 } : {}) }}
      />
    );
  };

  return (
    <Box>
      <Box role="group" aria-label="Show on the timeline" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
        {chip(null, 'Everything', page?.counts ? all : undefined)}
        {TIMELINE_KINDS.map(({ kind: value, label }) => chip(value, label, page?.counts ? (counts.get(value) ?? 0) : undefined))}
      </Box>

      {page && page.contactIds.length > 1 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          Includes the history of {page.contactIds.length - 1} record{page.contactIds.length > 2 ? 's' : ''} merged into this one.
        </Typography>
      )}

      {error && !page && <Alert severity="error">{describeListError(error).replace('contacts', 'the timeline')}</Alert>}

      {firstLoad && (
        <Box role="status" aria-label="Loading the timeline…">
          {Array.from({ length: 4 }, (_, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1.5, py: 1.25 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="70%" />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {page && page.items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {kind
            ? `No ${TIMELINE_KINDS.find((k) => k.kind === kind)?.label.toLowerCase()} for this person.`
            : 'Nothing has happened with this person yet.'}
        </Typography>
      )}

      {page &&
        byDay(page.items).map((day) => (
          <Box key={day.heading} component="section" aria-label={day.heading}>
            <Typography
              variant="overline"
              color="text.secondary"
              component="h3"
              sx={{ display: 'block', lineHeight: 2, mt: 1, borderBottom: '1px solid #eef1f5' }}
            >
              {day.heading}
            </Typography>
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {day.entries.map((entry) => (
                <Entry key={entry.id} entry={entry} />
              ))}
            </Box>
          </Box>
        ))}

      {page?.hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={showOlder}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} color="inherit" aria-label="Loading" /> : undefined}
          >
            Show older
          </Button>
        </Box>
      )}
      {error && page && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load older entries. Try again.
        </Alert>
      )}
    </Box>
  );
};
